import {
  defineFailureDomain,
  defineFailureMessageMap,
  DomainFailureError,
  type DomainFailure,
} from "#/shared/lib/failure";
import { isRecord } from "#/shared/lib/is-record";
import {
  errAsync,
  getUnknownErrorMessage,
  okAsync,
  ResultAsync,
} from "#/shared/lib/result";

import {
  BackendHttpError,
  BackendResponseParseError,
  readBackendHttpErrorPayload,
  requestBackendJson,
  type BackendFetch,
  type BackendJsonMethod,
} from "./client";
import { readBackendErrorMessage } from "./read-error-message";

/**
 * admin JSON API 调用的共享失败分类。所有 feature 共用这一组传输层变体；
 * feature 只在存在真正领域语义时才在其上扩展自己的失败类型。
 */
export type BackendFailure<Op extends string = string> =
  | (DomainFailure<"backend", "backend.forbidden", Op> & {
      retryable: boolean;
      status: number;
    })
  | (DomainFailure<"backend", "backend.network", Op> & { retryable: true })
  | (DomainFailure<"backend", "backend.rejected", Op> & {
      retryable: boolean;
      status: number;
    })
  | (DomainFailure<"backend", "backend.schema", Op> & { retryable: false })
  | (DomainFailure<"backend", "backend.unexpected", Op> & {
      retryable: false;
    });

const backendFailureDomain = defineFailureDomain("backend");

/** 读路径需要向 react-query 抛错时，BackendFailure 的唯一 Error 载体。 */
export class BackendFailureError<
  Op extends string = string,
> extends DomainFailureError<BackendFailure<Op>> {
  readonly retryable: boolean;

  constructor(detail: BackendFailure<Op>) {
    super(detail, "BackendFailureError");
    this.retryable = detail.retryable;
  }
}

/** 把一次后端请求抛出的未知异常归入共享传输失败分类。 */
export function classifyBackendFailure<Op extends string>(
  operation: Op,
  cause: unknown,
): BackendFailure<Op> {
  if (cause instanceof BackendHttpError) {
    return createBackendHttpFailure(operation, cause);
  }

  if (cause instanceof BackendResponseParseError) {
    return createBackendSchemaFailure(operation, cause.cause);
  }

  return createBackendNetworkFailure(operation, cause);
}

function createBackendHttpFailure<Op extends string>(
  operation: Op,
  error: BackendHttpError,
): BackendFailure<Op> {
  const cause = readBackendHttpErrorPayload(error);
  // 后端那句话只进 technicalMessage（日志和 devtools 读），永远不进商家的屏幕：
  // 它可能是 BFF 的 "Invalid query"、上游的 500 页面，或者一段 zod 报错。
  const technicalMessage = readBackendErrorMessage(cause);

  return backendFailureDomain.create({
    cause,
    code:
      error.status === 401 || error.status === 403
        ? ("backend.forbidden" as const)
        : ("backend.rejected" as const),
    operation,
    retryable: error.status >= 500,
    status: error.status,
    technicalMessage:
      technicalMessage ?? `backend request failed: ${error.status}`,
  });
}

function createBackendSchemaFailure<Op extends string>(
  operation: Op,
  cause: unknown,
): BackendFailure<Op> {
  return backendFailureDomain.create({
    cause,
    code: "backend.schema",
    operation,
    retryable: false,
    technicalMessage: getUnknownErrorMessage(cause),
  });
}

function createBackendNetworkFailure<Op extends string>(
  operation: Op,
  cause: unknown,
): BackendFailure<Op> {
  return backendFailureDomain.create({
    cause,
    code: "backend.network",
    operation,
    retryable: true,
    technicalMessage: getUnknownErrorMessage(cause),
  });
}

/** 供 resource mutation 的 normalizeUnexpectedFailure 使用的缺省实现。 */
export function createBackendUnexpectedFailure<Op extends string>(
  operation: Op,
  cause: unknown,
): BackendFailure<Op> {
  return backendFailureDomain.create({
    cause,
    code: "backend.unexpected",
    operation,
    retryable: false,
    technicalMessage: getUnknownErrorMessage(cause),
  });
}

/**
 * admin JSON API 的统一入口：请求、JSON 解析、契约 schema 归一化、失败分类一次完成。
 * feature 的请求函数应当只是一次本函数调用。
 */
export interface BackendResultRequest<TData, Op extends string> {
  /** 以 JSON 发送的请求体；省略则不发送 body。 */
  body?: unknown;
  /**
   * 原样发送的请求体，给上传文件用：不做 JSON 序列化，Content-Type 由调用点给。
   * 与 `body` 二选一。
   */
  rawBody?: BodyInit;
  fetch?: BackendFetch;
  headers?: HeadersInit;
  method: BackendJsonMethod;
  operation: Op;
  /** DTO 归一化（`@go-novus/admin-api` 的 zod parse），抛错归类为 backend.schema。 */
  parse: (payload: unknown) => TData;
  /** 路径来自 `@go-novus/admin-api` 的 `paths`，不要在调用点拼字符串。 */
  path: string;
  signal?: AbortSignal;
}

export function requestBackendResult<TData, Op extends string>(
  request: BackendResultRequest<TData, Op>,
): ResultAsync<TData, BackendFailure<Op>> {
  try {
    return ResultAsync.fromPromise(
      requestBackendJson(request.path, request.method, {
        body:
          request.rawBody ??
          (request.body === undefined
            ? undefined
            : JSON.stringify(request.body)),
        fetch: request.fetch,
        headers: mergeBackendRequestHeaders(request.headers, request.body),
        signal: request.signal,
      }),
      (error) => classifyBackendFailure(request.operation, error),
    ).andThen((payload) => {
      try {
        return okAsync(request.parse(payload));
      } catch (error: unknown) {
        return errAsync(createBackendSchemaFailure(request.operation, error));
      }
    });
  } catch (error: unknown) {
    return errAsync(classifyBackendFailure(request.operation, error));
  }
}

function mergeBackendRequestHeaders(
  requestHeaders: HeadersInit | undefined,
  body: unknown,
): Headers {
  const headers = new Headers(requestHeaders);

  if (body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

/** 读路径 queryFn 用：成功取值，失败抛出 BackendFailureError。 */
export function unwrapBackendResult<TValue, Op extends string>(
  result: ResultAsync<TValue, BackendFailure<Op>>,
): Promise<TValue> {
  return result.match((value) => value, throwBackendFailure);
}

/** 非 resource 读路径把类型化后端失败转换为异常边界时使用。 */
export function throwBackendFailure<Op extends string>(
  failure: BackendFailure<Op>,
): never {
  throw new BackendFailureError(failure);
}

/** 从 unknown error 中取回后端失败，不推断具体 operation。 */
export function readBackendFailure(error: unknown): BackendFailure | null;
/**
 * 从 unknown error 中取回后端失败，并以运行时 operation 白名单安全收窄类型。
 * 白名单不匹配时返回 null，避免调用方仅靠泛型参数自证来源。
 */
export function readBackendFailure<const Op extends string>(
  error: unknown,
  expectedOperations: readonly Op[],
): BackendFailure<Op> | null;
export function readBackendFailure(
  error: unknown,
  expectedOperations?: readonly string[],
): BackendFailure | null {
  if (isBackendFailureError(error)) {
    const failure = error.detail;

    if (
      expectedOperations !== undefined &&
      !expectedOperations.includes(failure.operation)
    ) {
      return null;
    }

    return failure;
  }

  // mutation execute 的 Err 载荷是裸 BackendFailure，没有 Error 包装。
  if (!isBackendFailure(error)) {
    return null;
  }

  if (
    expectedOperations !== undefined &&
    !expectedOperations.includes(error.operation)
  ) {
    return null;
  }

  return error;
}

function isBackendFailureError(error: unknown): error is BackendFailureError {
  return error instanceof BackendFailureError;
}

/** 判定任意值是否为后端失败本身（而非包装它的 Error），不推断具体 operation。 */
export function isBackendFailure(value: unknown): value is BackendFailure;
/**
 * 判定并以运行时 operation 白名单收窄。调用方从 unknown 边界捞到失败值时，
 * 用这个入口而不是自行枚举 code 与字段形状。
 */
export function isBackendFailure<const Op extends string>(
  value: unknown,
  expectedOperations: readonly Op[],
): value is BackendFailure<Op>;
export function isBackendFailure(
  value: unknown,
  expectedOperations?: readonly string[],
): boolean {
  if (!isRecord(value) || typeof value.operation !== "string") {
    return false;
  }

  if (
    expectedOperations !== undefined &&
    !expectedOperations.includes(value.operation)
  ) {
    return false;
  }

  switch (value.code) {
    case "backend.forbidden":
    case "backend.rejected":
      return isBackendHttpFailureShape(value);
    case "backend.network":
      return value.retryable === true;
    case "backend.schema":
    case "backend.unexpected":
      return value.retryable === false;
    default:
      return false;
  }
}

function isBackendHttpFailureShape(value: Record<string, unknown>): boolean {
  return (
    typeof value.status === "number" && typeof value.retryable === "boolean"
  );
}

/** 读查询的共享重试策略：最多重试两次，且仅限可重试的后端失败。 */
export function shouldRetryBackendRead(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    failureCount < 2 && error instanceof BackendFailureError && error.retryable
  );
}

/**
 * 各 feature 的本地化文案。本模块不持有任何产品文案，
 * 文案由消费方（feature UI 边界）传入。
 *
 * 商家看到的每一句都出自这里。后端、BFF、上游代理说了什么都不往屏幕上放——
 * 那些话是写给开发看的，而且没人能保证它是英文、是完整句子、不带内部细节。
 */
export interface BackendFailureMessages<Op extends string = string> {
  forbidden: (failure: BackendFailure<Op>) => string;
  network: (failure: BackendFailure<Op>) => string;
  rejected: (failure: BackendFailure<Op>) => string;
  schema: (failure: BackendFailure<Op>) => string;
  unexpected: (failure: BackendFailure<Op>) => string;
}

export type BackendFailureMessageResolver<Op extends string = string> = (
  failure: BackendFailure<Op> | null | undefined,
) => string | null;

export function createBackendFailureMessageResolver<Op extends string = string>(
  messages: BackendFailureMessages<Op>,
): BackendFailureMessageResolver<Op> {
  const resolveKnownFailure = defineFailureMessageMap<BackendFailure<Op>>({
    "backend.forbidden": (failure) => messages.forbidden(failure),
    "backend.network": (failure) => messages.network(failure),
    "backend.rejected": (failure) => messages.rejected(failure),
    "backend.schema": (failure) => messages.schema(failure),
    "backend.unexpected": (failure) => messages.unexpected(failure),
  });

  return (failure) => {
    if (failure === null || failure === undefined) {
      return null;
    }

    return resolveKnownFailure(failure);
  };
}
